// phase-compiler-tab-pins — the compiler workspace MAIN tabs and the canonical
// helpers that resolve/migrate legacy stored selections AND the per-tab pin set
// onto them.
//
// Before the six-tab consolidation the compiler nested a middle-pane tab strip
// (placement/classification/preview/package/links) and a right-pane Inspector
// with five top tabs (document/navigation/validation/lifecycle/operations) and
// six document sub-tabs. The six-tab consolidation replaced that whole
// hierarchy. This phase restores the CTD tree as a first-class pinnable tab
// (it had been a bespoke side panel) and restores a PIN affordance on EVERY
// tab: a pinned tab stays visible while another tab is active, so the active +
// pinned tabs render as a union of simultaneously-mounted panes.
//
// Old sessionStorage can still carry a pre-consolidation shape (or the
// intermediate six-tab shape) across a deploy within one session, so single
// helpers map every legacy key onto a workspace tab AND onto the pin set, each
// with a TOTAL fallback — an unmapped combination must never leave the
// workspace blank (it lands on Documents) and the pin set always resolves (a
// brand-new session has the CTD tree pinned by default).

export type WorkspaceTabKey =
  | 'tree'
  | 'documents'
  | 'review'
  | 'bookmarks-links'
  | 'validation'
  | 'lifecycle'
  | 'package';

export const WORKSPACE_TABS: ReadonlyArray<{ key: WorkspaceTabKey; label: string }> = [
  { key: 'tree', label: 'CTD Tree' },
  { key: 'documents', label: 'Documents' },
  { key: 'review', label: 'Review' },
  { key: 'bookmarks-links', label: 'Bookmarks & Links' },
  { key: 'validation', label: 'Validation' },
  { key: 'lifecycle', label: 'Lifecycle' },
  { key: 'package', label: 'Package' },
];

/** The total fallback: no signal (or an unrecognised one) lands on Documents. */
export const DEFAULT_WORKSPACE_TAB: WorkspaceTabKey = 'documents';

export function isWorkspaceTab(value: unknown): value is WorkspaceTabKey {
  return typeof value === 'string' && WORKSPACE_TABS.some((tab) => tab.key === value);
}

/** The legacy per-submission selections that predate the six-tab workspace. */
export interface LegacyWorkspaceSelection {
  readonly activePane?: unknown;
  readonly activeMiddleTab?: unknown;
  readonly inspectorTab?: unknown;
  readonly inspectorTier2?: unknown;
}

/** Inspector top tab (+ its document sub-tab) → workspace tab. */
function fromInspector(inspectorTab: unknown, inspectorTier2: unknown): WorkspaceTabKey | null {
  switch (inspectorTab) {
    case 'navigation':
      // The bookmark/hyperlink editors now live on Bookmarks & Links.
      return 'bookmarks-links';
    case 'validation':
      return 'validation';
    case 'lifecycle':
      return 'lifecycle';
    case 'operations':
      // Operations was split: snapshot → Bookmarks & Links, release/QC →
      // Validation, conversion/override → Documents. Validation is the honest
      // landing for the release/QC sign-off that dominated the old tab.
      return 'validation';
    case 'document':
      switch (inspectorTier2) {
        case 'preview':
        case 'compare':
        case 'annotations':
          return 'review';
        case 'package':
          return 'package';
        case 'placement':
        case 'classification':
          return 'documents';
        default:
          return 'documents';
      }
    default:
      return null;
  }
}

/** Middle-pane tab → workspace tab. */
function fromMiddle(activeMiddleTab: unknown): WorkspaceTabKey | null {
  switch (activeMiddleTab) {
    case 'placement':
    case 'classification':
      return 'documents';
    case 'preview':
      return 'review';
    case 'package':
      return 'package';
    case 'links':
      return 'validation';
    default:
      return null;
  }
}

/**
 * Derive the workspace tab from legacy selections, honouring which pane the
 * user was last on for precedence. Returns null when no legacy signal exists
 * (the caller then keeps its own default rather than forcing a tab).
 */
export function deriveWorkspaceTab(legacy: LegacyWorkspaceSelection): WorkspaceTabKey | null {
  const inspector = fromInspector(legacy.inspectorTab, legacy.inspectorTier2);
  const middle = fromMiddle(legacy.activeMiddleTab);
  if (legacy.activePane === 'documents') return middle ?? inspector ?? null;
  if (legacy.activePane === 'inspector') return inspector ?? middle ?? null;
  // Tree / unknown pane: the richer Inspector selection wins, then the middle.
  return inspector ?? middle ?? null;
}

/**
 * Resolve the canonical workspace tab for a submission from the persisted view
 * map: an explicit (valid) `workspaceTab` always wins; otherwise a legacy
 * selection is migrated; otherwise the total fallback (Documents).
 */
export function resolveWorkspaceTab(views: ReadonlyMap<string, unknown>, id: string): WorkspaceTabKey {
  const explicit = views.get(`${id}:workspaceTab`);
  if (isWorkspaceTab(explicit)) return explicit;
  const derived = deriveWorkspaceTab({
    activePane: views.get(`${id}:activePane`),
    activeMiddleTab: views.get(`${id}:activeMiddleTab`),
    inspectorTab: views.get(`${id}:inspectorTab`),
    inspectorTier2: views.get(`${id}:inspectorTier2`),
  });
  return derived ?? DEFAULT_WORKSPACE_TAB;
}

// ── Pins — the canonical per-submission pin set ──────────────────────────────
//
// A pinned tab stays visible while another tab is active. The pin set is stored
// as a canonical comma-joined string of tab keys (`workspacePins`). An empty
// string is a VALID stored value meaning "nothing pinned" — distinct from an
// absent key, which means "no signal, use the default (tree pinned)".

/** A brand-new session pins the CTD tree so it is visible beside the first tab. */
export const DEFAULT_WORKSPACE_PINS: ReadonlySet<WorkspaceTabKey> = new Set<WorkspaceTabKey>(['tree']);

/** Parse a stored pin string into a validated, deduplicated set (canonical order honoured on serialize). */
export function parseWorkspacePins(value: unknown): Set<WorkspaceTabKey> {
  const pins = new Set<WorkspaceTabKey>();
  if (typeof value !== 'string') return pins;
  for (const part of value.split(',')) if (isWorkspaceTab(part)) pins.add(part);
  return pins;
}

/** Serialize a pin set to the canonical comma-joined string (WORKSPACE_TABS order). */
export function serializeWorkspacePins(pins: ReadonlySet<WorkspaceTabKey>): string {
  return WORKSPACE_TABS.filter((t) => pins.has(t.key)).map((t) => t.key).join(',');
}

/**
 * Resolve the canonical pin set for a submission from the persisted view map.
 * Precedence:
 *   1. An explicit valid `workspacePins` string wins entirely (including empty).
 *   2. Otherwise the legacy pins are migrated: the pre-consolidation
 *      `pinnedPanes` list (tree / documents / inspector) maps each pane onto a
 *      workspace tab, and an explicit six-tab-era `treePinned` boolean overrides
 *      tree membership.
 *   3. With NO pin signal at all, the default (CTD tree pinned) — never blank.
 */
export function resolveWorkspacePins(views: ReadonlyMap<string, unknown>, id: string): Set<WorkspaceTabKey> {
  const explicit = views.get(`${id}:workspacePins`);
  if (typeof explicit === 'string') return parseWorkspacePins(explicit);

  const legacyList = views.get(`${id}:pinnedPanes`);
  const treePinned = views.get(`${id}:treePinned`);
  const treeVisible = views.get(`${id}:treeVisible`);
  const hasList = typeof legacyList === 'string';
  const hasTreePin = typeof treePinned === 'boolean';
  const hasTreeVisible = typeof treeVisible === 'boolean';
  if (!hasList && !hasTreePin && !hasTreeVisible) return new Set(DEFAULT_WORKSPACE_PINS);

  const pins = new Set<WorkspaceTabKey>();
  if (hasList) {
    const legacy: LegacyWorkspaceSelection = {
      activePane: views.get(`${id}:activePane`),
      activeMiddleTab: views.get(`${id}:activeMiddleTab`),
      inspectorTab: views.get(`${id}:inspectorTab`),
      inspectorTier2: views.get(`${id}:inspectorTier2`),
    };
    for (const pane of (legacyList as string).split(',')) {
      if (pane === 'tree') pins.add('tree');
      else if (pane === 'documents') pins.add('documents');
      else if (pane === 'inspector') {
        // The old Inspector collapsed into several tabs. Only migrate its pin
        // when the legacy selection unambiguously derives a tab; a null signal
        // is SKIPPED rather than defaulted to Documents, so migration never
        // invents a pin the operator never set (Kimi F4).
        const derived = deriveWorkspaceTab(legacy);
        if (derived) pins.add(derived);
      }
    }
  }
  // Tree membership from the six-tab era. The new model has no separate
  // "open but unpinned" state — a tab is visible only when active or pinned —
  // so a tree that was EITHER pinned OR merely open (treeVisible) is preserved
  // by pinning it, rather than silently lost (Kimi F5). An explicit
  // treePinned=false with the tree NOT open removes it. Only the tree is ever
  // touched here, so migration never pins a wrong tab.
  if (hasTreePin || hasTreeVisible) {
    const keepTree = treePinned === true || treeVisible === true;
    if (keepTree) pins.add('tree');
    else pins.delete('tree');
  }
  return pins;
}
